/**
 * Popup that lists autocomplete suggestions for the current `{{ … }}` span.
 *
 * Pure presentational: receives the list of items + an `onSelect` callback;
 * caret/text-mutation logic lives in ``ExpressionTextField``. Items are
 * grouped (functions / variables / trigger / nodes / roots) with a sticky
 * header per section.
 */

import { FC, useEffect, useRef } from 'react'
import { useTranslate } from 'react-admin'
import Popper from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { themeColor } from '../../../layout/theme/color'
import type { AutocompleteItem } from './useExpressionAutocomplete'

interface ExpressionAutocompletePopperProps {
  anchorEl: HTMLElement | null
  open: boolean
  items: AutocompleteItem[]
  highlightedIndex: number
  functionsLoadFailed?: boolean
  onHover: (index: number) => void
  onSelect: (item: AutocompleteItem) => void
}

const GROUP_LABELS: Record<AutocompleteItem['group'], string> = {
  roots: 'Namespaces',
  functions: 'Built-in functions',
  variables: 'Variables',
  trigger: 'Trigger input',
  nodes: 'Upstream nodes',
}

const ExpressionAutocompletePopper: FC<ExpressionAutocompletePopperProps> = ({
  anchorEl,
  open,
  items,
  highlightedIndex,
  functionsLoadFailed = false,
  onHover,
  onSelect,
}) => {
  const translate = useTranslate()
  const listRef = useRef<HTMLUListElement>(null)

  // Keep the highlighted item scrolled into view as the user arrows down.
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const el = list.querySelector<HTMLElement>(`[data-idx="${highlightedIndex}"]`)
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex])

  // Show the popup whenever the caret is inside a {{ … }} span, even when
  // there are no items — that lets us surface a "functions list unavailable"
  // hint while the user is mid-typing ``functions.``.
  const hasContent = items.length > 0 || functionsLoadFailed
  if (!open || !anchorEl || !hasContent) return null

  const groups: { group: AutocompleteItem['group']; items: AutocompleteItem[] }[] = []
  items.forEach((item) => {
    const last = groups[groups.length - 1]
    if (last && last.group === item.group) {
      last.items.push(item)
    } else {
      groups.push({ group: item.group, items: [item] })
    }
  })

  let runningIndex = -1

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1500 }}
      modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
    >
      <Paper
        elevation={6}
        sx={{
          minWidth: 320,
          maxWidth: 480,
          maxHeight: 320,
          overflow: 'auto',
          borderRadius: '10px',
          border: `1px solid ${themeColor.slate10}`,
        }}
      >
        <MenuList ref={listRef} dense sx={{ py: 0.5 }}>
          {groups.map((g) => (
            <Box key={g.group}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1.5,
                  py: 0.5,
                  fontWeight: 600,
                  color: themeColor.slate60,
                  textTransform: 'uppercase',
                  fontSize: 10,
                  letterSpacing: 0.5,
                  // eslint-disable-next-line quote-props
                  background: themeColor.slate05,
                }}
              >
                {GROUP_LABELS[g.group]}
              </Typography>
              {g.items.map((item) => {
                runningIndex += 1
                const idx = runningIndex
                const active = idx === highlightedIndex
                return (
                  <MenuItem
                    key={`${g.group}-${item.insert}`}
                    data-idx={idx}
                    selected={active}
                    onMouseEnter={() => onHover(idx)}
                    onMouseDown={(e) => {
                      // mousedown — not click — so the TextField doesn't blur
                      // before we get a chance to apply the suggestion.
                      e.preventDefault()
                      onSelect(item)
                    }}
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 0.25,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', width: '100%' }}>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: '"SF Mono", "Fira Code", monospace',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.detail && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 11,
                            color: themeColor.slate60,
                            fontFamily:
                              g.group === 'functions'
                                ? '"SF Mono", "Fira Code", monospace'
                                : 'inherit',
                          }}
                        >
                          {item.detail}
                        </Typography>
                      )}
                    </Box>
                    {item.doc && (
                      <Typography
                        component="span"
                        sx={{ fontSize: 11, color: themeColor.slate60, lineHeight: 1.3 }}
                      >
                        {item.doc}
                      </Typography>
                    )}
                  </MenuItem>
                )
              })}
            </Box>
          ))}
        </MenuList>
        {functionsLoadFailed && (
          <Box
            sx={{
              borderTop: `1px solid ${themeColor.slate10}`,
              px: 1.5,
              py: 0.75,
              background: themeColor.slate05,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: themeColor.slate60, fontSize: 11, lineHeight: 1.3 }}
            >
              {translate('workflow.config.autocompleteFunctionsLoadFailed')}
            </Typography>
          </Box>
        )}
      </Paper>
    </Popper>
  )
}

export default ExpressionAutocompletePopper
